import { DateFormat, FilterSettings, Geometry } from '@range.io/basic-types'
import { pluck, uniq, without } from '@range.io/functional'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 } from 'uuid'
import { FlexRow } from '../components-reusable/Flex.js'
import useBulkSelectMode from '../components-reusable/hooks/useBulkSelectMode.js'
import { useNavigateToCollaboration } from '../components-reusable/hooks/useNavigateToCollaboration.js'
import useRecentCollaborations from '../components-reusable/hooks/useRecentCollaborations.js'
import {
    CollaborationChangedCommand,
    GeometriesChangedCommand,
    GeometriesRemovedCommand,
} from '../firebase/commands/index.js'
import { useCommandHistory } from '../firebase/commands/UndoRedo.js'
import { convertIdsToTagsObjects, saveAs } from '../helpers.js'
import { styled } from '../range-theme/index.js'
import { ReduxActions, ReduxSelectors } from '../redux/index.js'
import * as Segment from '../segment/segment.js'
import CollaborationWindow from './CollaborationWindow.js'
import ControlBar from './ControlBar.js'
import EnrichedCollaboration from './enriched-collaboration.js'
import ListViewMain from './ListViewMain'
import ProjectPage, { PageContent } from './ProjectPage.js'

const StyledCollaborationWindowContainer = styled(FlexRow, {
    minWidth: '500px',
    width: '500px',
    height: '100%',
    marginLeft: '12px',
    boxSizing: 'border-box',
})

/**
 * Sort items by field or subfield.
 * @param  {Array} items
 * @param  {Object} sortSettings {field, isAscending}
 * @param  {string?} innerField
 * @return {Array} the items array sorted
 */
const sortByField = ({ items, sortSettings, innerField }) => {
    const { field, isAscending } = sortSettings
    const isActuallyANumber = str => !isNaN(parseFloat(str)) // check if string can be turned to a number

    return items.sort((a, b) => {
        const valA = innerField ? a[field]?.[innerField] : a[field]
        const valB = innerField ? b[field]?.[innerField] : b[field]

        // some fields are stored as strings, but actually are numbers, i.e. identifiers
        // so those shouldn't be compared as strings
        if (typeof valA === 'string' && !isActuallyANumber(valA)) {
            // Explicitly check for undefined values
            if (typeof valA === 'undefined') return isAscending ? -1 : 1
            if (typeof valB === 'undefined') return isAscending ? 1 : -1

            // Perform case insensitive comparison
            if (isAscending) {
                return valA.toLowerCase().localeCompare(valB.toLowerCase())
            } else {
                return -valA.toLowerCase().localeCompare(valB.toLowerCase())
            }
        } else {
            if (!valA) return isAscending ? -1 : 1
            if (!valB) return isAscending ? 1 : -1
            if (isAscending) return valA - valB
            return valB - valA
        }
    })
}

const convertToCSV = jsonData => {
    const array = [Object.keys(jsonData[0])].concat(jsonData)
    return array
        .map(row => {
            return Object.values(row)
                .map(value => `"${value}"`)
                .join(',')
        })
        .join('\n')
}

export const LIST_VIEW_SORT_TYPES = {
    taskName: 'name',
    assigneeName: 'assignee',
    categoryName: 'categoryName',
    dueDate: 'dueDate',
    locationName: 'sheetLabel',
    creationDate: 'date',
    identifier: 'identifier',
}

const DEFAULT_SORT_SETTINGS = { field: 'mostRecentUpdateDate', isAscending: false }

const ListView = () => {
    const toReports = () => navigate(`/${organizationId}/reports`)

    const { runCommand } = useCommandHistory()
    const { getState, dispatch } = useStore()

    const allCanvases = useSelector(ReduxSelectors.canvasesAsPOJO)
    const selectedCollaboration = useSelector(ReduxSelectors.selectedCollaboration)
    const collaborationUpdates = useSelector(ReduxSelectors.recentCollaborationUpdateLookup)
    const allCollaborations = useSelector(ReduxSelectors.collaborationsAsObject)
    const projectParticipants = useSelector(ReduxSelectors.selectedProjectParticipantsAsArray)
    const currentUserId = ReduxSelectors.selectedUserId(getState())
    const selectedOrganization = useSelector(ReduxSelectors.selectedOrganization)
    const user = selectedOrganization?.participants[currentUserId]
    const tagNameLookup = useSelector(ReduxSelectors.tagNameAsPOJO)
    const selectedProject = useSelector(ReduxSelectors.selectedProject)

    const navigate = useNavigate()
    const { workspaceId: organizationId, projectId } = useParams()
    const { navigateToCollaboration } = useNavigateToCollaboration()
    const filteredTaskList = useSelector(ReduxSelectors.filteredTaskList)
    const [searchPhrase, setSearchPhrase] = useState(null)
    const [checkedItems, setCheckedItems] = useState([])
    const [bulkActionSelectedAssignee, setBulkActionSelectedAssignee] = useState()
    const [areAllCheckedTasksArchived, setAreAllCheckedTasksArchived] = useState(false)
    const [bulkActionSelectedStatus, setBulkActionSelectedStatus] = useState()
    const [sortSettings, setSortSettings] = useState(DEFAULT_SORT_SETTINGS)
    const [isFollowingInProgress, setIsFollowingInProgress] = useState(false)
    const taskRowCheckboxDataId = 'task-row-checkbox'
    const isInBulkSelectMode = useBulkSelectMode()
    const wasSegmentEventSent = useRef(false)

    const { registerCollaborationAsRecentlyViewed } = useRecentCollaborations()

    // check if current user is allowed to archive geometries
    const hasRightsToArchive = useSelector(ReduxSelectors.isUpdateAllowed('geometry'))

    const canFullyUpdateCollaboration = useMemo(() => {
        if (!Object.keys(allCollaborations).length) return false

        if (!checkedItems.length) {
            const collaboration = Object.values(allCollaborations)[0]
            return ReduxSelectors.isUpdateAllowed('collaboration', collaboration)(getState())
        } else {
            const collaboration = allCollaborations[checkedItems[0]]
            return ReduxSelectors.isUpdateAllowed('collaboration', collaboration)(getState())
        }
    }, [checkedItems, allCollaborations])

    const allStatusNames = useSelector(ReduxSelectors.statusNamesAsLookupTable)
    const availableStatuses = useMemo(() => {
        const sortedStatuses = Object.values(allStatusNames)
            .sort((a, b) => a.order - b.order) // sort statuses by order
            .reduce((acc, status) => ({ ...acc, [status.id]: status }), {})

        if (canFullyUpdateCollaboration) return sortedStatuses

        return Object.keys(sortedStatuses).reduce((acc, statusId) => {
            acc[statusId] = { ...sortedStatuses[statusId] }

            // mark statuses that complete collaboration as disabled
            if (acc[statusId].isCompleted) {
                acc[statusId].disabled = true
            }

            return acc
        }, {})
    }, [allStatusNames, user, canFullyUpdateCollaboration])

    const [lastClickedCheckboxId, setLastClickedCheckboxId] = useState(false)

    // Filter out suspended users and sort alphabetically
    const allAvailableAssignees = projectParticipants
        .filter(participant => !participant.isSuspended)
        .sort((a, b) => a.name?.localeCompare(b.name))

    // Check if any filters were applied
    const filterSettings = useSelector(ReduxSelectors.taskListFilterSettings)
    const allStatuses = useSelector(ReduxSelectors.statusNamesAsLookupTable)
    const allParticipants = useSelector(ReduxSelectors.participantsAsLookupTable)
    const project = useSelector(ReduxSelectors.selectedProject)

    const areFiltersApplied = !FilterSettings.isSetToDefaults(filterSettings, allStatuses, allParticipants)

    const enrichedCollaborations = useMemo(() => {
        const filterBySearchPhrase = item => item.name.toLowerCase().includes(searchPhrase)

        const enrichCollaboration = collaboration => {
            const state = getState()
            const tagNames = collaboration.tagNames.map(tag => tag.name)
            const canvas = Object.values(allCanvases).filter(canvas => canvas.id === collaboration.canvasId)[0]
            const assignee = collaboration.assignee
                ? ReduxSelectors.selectedProjectParticipantWithId(state, collaboration.assignee?.id)
                : null
            const assigneeIsOnline = !!presentParticipants.find(u => u.id === collaboration.assignee?.id)
            const projectIdentifier = ReduxSelectors.selectedProject(state)?.identifier

            const category = ReduxSelectors.categoryForCollaboration(state, collaboration)

            return EnrichedCollaboration.from({
                ...collaboration,
                assigneeAvatarUrl: assignee?.avatarUrl,
                assigneeEmail: assignee?.email,
                assigneeIsOnline,
                assigneeName: collaboration.assignee?.fullName,
                category,
                categoryName: category?.name || '', // categoryId is technically optional for now
                createdByName: collaboration.createdBy?.fullName,
                mostRecentUpdateDate: collaborationUpdates[collaboration.id],
                sheetLabel: canvas.name,
                tagNames,
                projectName: project.name,
                projectIdentifier,
                canvasId: canvas.id,

                // TODO: we should not be creating a random amalgamation of things into an "item"
                // copy from Collaboration into the result
                isArchived: collaboration.isArchived,
                assignee: collaboration.assignee,
                geometry: collaboration.geometry,
                status: collaboration.statusName,
                url: `${window.location.origin}/${collaboration.organizationId}/${collaboration.projectId}/${collaboration.canvasId}?c=${collaboration.id}`,
            })
        }

        const presentParticipants = ReduxSelectors.presentProjectParticipants(getState())
        const collaborations = Object.values(filteredTaskList)
        const enrichedCollaborations = collaborations.map(enrichCollaboration)
        const filteredEnrichedCollaborations = searchPhrase?.length
            ? enrichedCollaborations.filter(filterBySearchPhrase)
            : enrichedCollaborations

        // ensure the assignee is sorted by fullName, not by comparing assignees objects
        const innerField = sortSettings.field === LIST_VIEW_SORT_TYPES.assigneeName ? 'fullName' : null

        return sortByField({ items: filteredEnrichedCollaborations, sortSettings, innerField })
    }, [collaborationUpdates, filteredTaskList, sortSettings, searchPhrase])

    // Adjust checked items when the list changes
    // e.g. after search or filter
    useEffect(() => {
        if (!checkedItems.length) return

        const enrichedCollaborationMap = new Map()
        enrichedCollaborations.forEach(c => enrichedCollaborationMap.set(c.id, c))

        const checkedItemsNew = []

        // Check if checked items contain item id that is displayed on the list.
        // If it is no longer displayed (e.g. after search is used),
        // that element should be removed from checked items.
        checkedItems.forEach(itemId => {
            if (enrichedCollaborationMap.get(itemId)) checkedItemsNew.push(itemId)
        })

        setCheckedItems(checkedItemsNew)
    }, [enrichedCollaborations])

    const handleSearchPhraseChanged = newSearchPhrase => {
        if (newSearchPhrase?.length) setSearchPhrase(newSearchPhrase.trim().toLowerCase())
        else setSearchPhrase(newSearchPhrase)
    }

    const handleGoToProjectCanvas = () => navigate(`/${organizationId}/${projectId}`)

    const handleItemSelect = itemId => {
        dispatch(ReduxActions.collaborationSelected({ id: itemId }))
        registerCollaborationAsRecentlyViewed(itemId)
    }

    const handleGoToCanvas = () => navigateToCollaboration(selectedCollaboration.id)

    const handleGoToCanvasId = collaborationId => navigateToCollaboration(collaborationId)

    const handleItemChecked = itemId => {
        setCheckedItems(checkedItemsArray => {
            const _idsToToggle = () => {
                const allIdsArray = pluck('id', enrichedCollaborations)

                // Get index of most recently checked/unchecked checkbox on the rows list
                const lastClickedItemIndex = allIdsArray.findIndex(id => id === lastClickedCheckboxId)

                // Get index of currently checked/unchecked item on the rows list
                const currentItemIndex = allIdsArray.findIndex(id => id === itemId)

                // Get all items between two points
                if (lastClickedItemIndex > currentItemIndex)
                    return allIdsArray.slice(currentItemIndex, lastClickedItemIndex)

                return allIdsArray.slice(lastClickedItemIndex, currentItemIndex)
            }

            // Check if item isn't already present in checked items.
            // If it isn't, it means user just checked it and we have to add it to checked items.
            const shouldAddItems = checkedItemsArray.indexOf(itemId) === -1

            if (!isInBulkSelectMode && shouldAddItems) return [...checkedItemsArray, itemId] // if we are not in bulk select mode, simply add new item as checked
            if (!isInBulkSelectMode) return without(itemId, checkedItemsArray)

            const idsToToggle = _idsToToggle()

            // Add all idsToToggle to the original checked items
            if (shouldAddItems) return uniq([...checkedItemsArray, ...idsToToggle, itemId, lastClickedCheckboxId])

            // uncheck all items in between last clicked checkbox and the checkbox that was clicked right now
            return without([...idsToToggle, itemId, lastClickedCheckboxId], checkedItemsArray)
        })

        setLastClickedCheckboxId(itemId)
        setBulkActionSelectedStatus(null)
        setBulkActionSelectedAssignee(null)
    }

    const clearCheckedItems = () => {
        setCheckedItems([])
        setBulkActionSelectedStatus(null)
        setBulkActionSelectedAssignee(null)
    }

    const showToast = ({ toastLabel, severity = 'success' }) => {
        dispatch(
            ReduxActions.toastAdded({
                id: checkedItems[0],
                toastLabel,
                severity,
                showUndo: false,
            })
        )
    }

    const getGeometryForTaskId = itemId => {
        const collaboration = allCollaborations[itemId]
        if (!collaboration) return null

        return ReduxSelectors.geometryForCollaboration(getState(), collaboration)
    }

    const isAnyCheckedTaskUnarchived = items =>
        items?.some(itemId => {
            const geometry = getGeometryForTaskId(itemId)
            return !!geometry.archivedDate === false
        })

    useEffect(() => {
        setAreAllCheckedTasksArchived(!isAnyCheckedTaskUnarchived(checkedItems))
    }, [checkedItems])

    const onDeleteItems = () => {
        // Go through each checked task id and remove its corresponding geometry
        const geometriesToDelete = []
        checkedItems?.forEach(itemId => {
            const geometry = getGeometryForTaskId(itemId)
            if (!geometry) return
            geometriesToDelete.push(geometry)
        })

        const itemsCount = geometriesToDelete.length
        const toastLabel =
            itemsCount > 1
                ? `${itemsCount} tasks have successfully been deleted`
                : `${itemsCount} task has successfully been deleted`

        runCommand(GeometriesRemovedCommand.Outbound(geometriesToDelete, toastLabel))
        setCheckedItems([]) // clear all selections
    }

    const onArchiveItems = () => {
        let itemsArchivedCount = 0

        checkedItems?.forEach(itemId => {
            const geometry = getGeometryForTaskId(itemId)

            // If task is already archived, skip updating it
            if (!geometry || (!areAllCheckedTasksArchived && geometry.archivedDate)) return

            const updatedGeometry = Geometry.update(geometry, {
                archivedDate: areAllCheckedTasksArchived ? undefined : new Date(), // Unarchive, when all checked tasks are already archived
            })
            itemsArchivedCount++
            runCommand(GeometriesChangedCommand.Outbound([updatedGeometry]))
        })

        setAreAllCheckedTasksArchived(!isAnyCheckedTaskUnarchived(checkedItems))

        const toastLabel =
            itemsArchivedCount > 1
                ? `${itemsArchivedCount} tasks have successfully been archived`
                : `${itemsArchivedCount} task has successfully been archived`

        showToast({ toastLabel })
    }

    const handleAssigneeChange = ({ userId, collaborationId }) => {
        if (!collaborationId) return false

        runCommand(CollaborationChangedCommand.Outbound(collaborationId, { assigneeId: userId }, currentUserId))
        return true
    }

    const onBulkActionSelectedAssignee = userId => {
        const participant = userId ? ReduxSelectors.selectedProjectParticipantWithId(getState(), userId) : null
        setBulkActionSelectedAssignee(participant)

        let changedTasks = 0

        checkedItems?.forEach(itemId => {
            const collaboration = allCollaborations[itemId]

            // Skip updating if selected user is already an assignee of this collaboration
            if (!collaboration || collaboration.assigneeId === userId) return

            const isSuccess = handleAssigneeChange({ collaborationId: collaboration.id, userId })
            if (isSuccess) changedTasks++
        })

        if (changedTasks) {
            const toastLabel =
                changedTasks > 1
                    ? `The Assignee for ${changedTasks} tasks has been successfully updated`
                    : `The Assignee for ${changedTasks} task has been successfully updated`

            showToast({ toastLabel })
        }
    }

    // convert the dropdown text back to a StatusName id since only the id is stored in a Collaboration
    const handleStatusChange = (collaborationId, statusId) =>
        runCommand(CollaborationChangedCommand.Outbound(collaborationId, { statusId }, currentUserId)) // update collaboration status

    const onBulkActionSelectedStatus = statusId => {
        setBulkActionSelectedStatus(statusId)

        let changedTasks = 0
        checkedItems?.forEach(itemId => {
            const collaboration = allCollaborations[itemId]
            const geometry = getGeometryForTaskId(itemId)

            // Skip updating if status is not archived and the status is already set as the one we want to switch to
            if (!geometry.archivedDate && collaboration.statusNameId === statusId) return

            const updatedGeometry = Geometry.update(geometry, {
                archivedDate: undefined, // unarchive task to make it visible back again
            })
            runCommand(GeometriesChangedCommand.Outbound([updatedGeometry])) // update geometry archived date
            runCommand(CollaborationChangedCommand.Outbound(collaboration.id, { statusId }, currentUserId)) // update collaboration status
            changedTasks++
        })

        const toastLabel =
            changedTasks > 1
                ? `The Status for ${changedTasks} tasks has been successfully updated`
                : `The Status for ${changedTasks} task has been successfully updated`

        showToast({ toastLabel })
    }

    const onBulkActionTagsChange = ({ items, editType }) => {
        const addTags = newTagIds => {
            checkedItems?.forEach(itemId => {
                const collaboration = allCollaborations[itemId]
                const newTags = convertIdsToTagsObjects(
                    uniq([...collaboration.tagNameIds, ...newTagIds]),
                    tagNameLookup
                )

                runCommand(
                    CollaborationChangedCommand.Outbound(
                        collaboration.id,
                        { tags: newTags },
                        currentUserId,
                        true /* skipUpdate */
                    )
                )
            })
        }

        const replaceTags = newTagIds => {
            checkedItems?.forEach(itemId => {
                const collaboration = allCollaborations[itemId]
                const newTags = convertIdsToTagsObjects(uniq(newTagIds), tagNameLookup)

                runCommand(
                    CollaborationChangedCommand.Outbound(
                        collaboration.id,
                        { tags: newTags },
                        currentUserId,
                        true /* skipUpdate */
                    )
                )
            })
        }

        if (editType === 'add') addTags(items)
        if (editType === 'replace') replaceTags(items)

        const changedTasks = checkedItems.length
        const toastLabel =
            changedTasks > 1
                ? `The Tags for ${changedTasks} tasks have been successfully updated`
                : `The Tags for ${changedTasks} task have been successfully updated`

        showToast({ toastLabel })
    }

    const handleDateChange = ({ itemId, dueDate }) => {
        const collaboration = allCollaborations[itemId]
        runCommand(CollaborationChangedCommand.Outbound(collaboration.id, { dueDate }, currentUserId))
    }

    const onBulkActionDateChange = dueDate => {
        if (!dueDate) return

        checkedItems?.forEach(itemId => {
            handleDateChange({ itemId, dueDate })
        })
        const changedTasks = checkedItems.length

        const toastLabel =
            changedTasks > 1
                ? `The Due Date for ${changedTasks} tasks has been successfully updated`
                : `The Due Date for ${changedTasks} task has been successfully updated`

        showToast({ toastLabel })
    }

    const handleCheckAllItemsClick = () => {
        if (enrichedCollaborations.length === checkedItems.length) setCheckedItems([])
        else setCheckedItems([...enrichedCollaborations.map(item => item.id)])
    }

    const handleSortClick = ({ type, isAscending }) => {
        if (isAscending === undefined) setSortSettings({ field: type, isAscending: true })
        else if (isAscending) setSortSettings({ field: type, isAscending: false })
        else setSortSettings(DEFAULT_SORT_SETTINGS)
    }

    const handleBulkFollowButtonClick = async () => {
        let modifiedItems = 0
        setIsFollowingInProgress(true)

        // Await all CollaborationChangedCommand.Outbound before showing the toast
        await Promise.all(
            checkedItems.map(itemId => {
                const followerIds = allCollaborations[itemId].followerIds
                const isAFollower = followerIds?.includes(currentUserId)

                // If not all selected tasks have current user as a follower
                // we want to modify only some to make all selected tasks followed by them.
                // In such case, we simply skip modifying the task which he is already following,
                // as there are no changes to be made.
                if (!areAllTasksFollowed && isAFollower) return Promise.resolve()

                modifiedItems++
                let newFollowerIds = []
                if (areAllTasksFollowed) newFollowerIds = without(currentUserId, followerIds)
                else if (!isAFollower) newFollowerIds = followerIds.concat(currentUserId)

                return runCommand(
                    CollaborationChangedCommand.Outbound(itemId, { followers: newFollowerIds }, currentUserId)
                )
            })
        )

        setIsFollowingInProgress(false)
        const toastLabel = areAllTasksFollowed ? `${modifiedItems} tasks unfollowed` : `${modifiedItems} tasks followed`
        showToast({ toastLabel })
    }

    /**
     * For all selected collaborations, get array of user ids and either add or remove them from collaborations followers.
     * @param {[uuid]} usersIds
     * @param {'add'|'remove'} mode
     */
    const handleBulkFollowersChange = async (usersIds, mode) => {
        setIsFollowingInProgress(true)

        // Await all CollaborationChangedCommand.Outbound before showing the toast
        await Promise.all(
            checkedItems.map(itemId => {
                const collaboration = allCollaborations[itemId]
                let newFollowerIds = collaboration.followerIds

                if (mode === 'add') newFollowerIds = uniq(collaboration.followerIds.concat(usersIds))
                else if (mode === 'remove') newFollowerIds = without(usersIds, collaboration.followerIds)
                else return Promise.resolve()

                return runCommand(
                    CollaborationChangedCommand.Outbound(itemId, { followers: newFollowerIds }, currentUserId)
                )
            })
        )

        setIsFollowingInProgress(false)
        showToast({ toastLabel: `Followers for selected tasks ${mode === 'add' ? 'added' : 'removed'}` })
    }

    const handleExport = () => {
        const buildItemForExport = itemId => {
            const item = enrichedCollaborations.find(i => i.id === itemId)
            const geometry = item.geometry
            const isOnSatelliteCanvas = geometry.isOnSatelliteCanvas()
            const exportObject = {
                'Project Name': item.projectName,
                'Task ID': `${selectedProject.identifier} - ${item.identifier}`,
                Category: item.category?.name || '',
                'Task Name': item.name.length > 0 ? item.name : 'Untitled task',
                'Task Status': item.status.name,
                'Created By': item.createdByName,
                'Created Date': DateFormat.primary(item.createdAt),
                Assignee: item.assigneeName || '',
                'Due Date': item.dueDate ? DateFormat.primary(item.dueDate) : '',
                Location: item.sheetLabel,
                'Task Description': item.description || '',
                Tags: (item.tagNames || []).join(';'),
                Latitude: '',
                Longitude: '',
                'Elevation Meters': '',
                'Elevation Feet': '',
                'Task URL': item.url,
            }

            if (isOnSatelliteCanvas) {
                const formattedGeometry = Geometry.getFormattedForDisplay(geometry)
                exportObject.Latitude = formattedGeometry.lat
                exportObject.Longitude = formattedGeometry.lng
                if (formattedGeometry?.elevationMetres) {
                    exportObject['Elevation Meters'] = formattedGeometry.elevationMetres
                    exportObject['Elevation Feet'] = formattedGeometry.elevationFeet
                }
            }

            return exportObject
        }

        const dataToExport = checkedItems.map(buildItemForExport)
        const csvData = convertToCSV(dataToExport)
        const blob = new Blob([csvData], { type: 'text/csv' })
        saveAs(blob, 'exported.csv')
    }

    const areAllTasksFollowed = React.useMemo(() => {
        if (!allCollaborations || !checkedItems.length) return false

        // if user doesn't follow one of the selected collaborations
        if (checkedItems.some(itemId => !allCollaborations[itemId].followerIds.includes(currentUserId))) return false

        return true
    }, [checkedItems, allCollaborations])

    const hasRightsToDelete = React.useMemo(() => {
        if (!user || !allCollaborations) return false

        // check if current user can delete all selected tasks
        const canManage = checkedItems.every(collaborationId =>
            ReduxSelectors.isDeleteAllowed('collaboration', allCollaborations[collaborationId])(getState())
        )

        return currentUserId && canManage
    }, [checkedItems, allCollaborations, currentUserId, user])

    const isProjectDataLoaded = ReduxSelectors.isProjectDataLoaded(getState())

    useEffect(() => {
        if (project && !wasSegmentEventSent.current) {
            const segmentParams = ReduxSelectors.paramsForTrackEvent(getState())
            const archiveItemCount = enrichedCollaborations.filter(item => item.isArchived).length
            segmentParams.activeTaskCount = enrichedCollaborations.length - archiveItemCount
            segmentParams.archiveTaskCount = archiveItemCount
            segmentParams.projectName = project.name
            Segment.sendTrack('project tasklist viewed', v4(), segmentParams)
            wasSegmentEventSent.current = true
        }
    }, [project, enrichedCollaborations])

    return (
        <ProjectPage data-cy="list-view" activeTab={ControlBar.TABS.LIST}>
            <PageContent css={{ flexDirection: 'row' }}>
                <ListViewMain
                    hasRightsToArchive={hasRightsToArchive}
                    hasRightsToDelete={hasRightsToDelete}
                    isProjectDataLoaded={isProjectDataLoaded}
                    enrichedCollaborations={enrichedCollaborations}
                    onGoToCanvas={handleGoToProjectCanvas}
                    onGoToCanvasId={handleGoToCanvasId}
                    onItemSelect={handleItemSelect}
                    showFullContent={!selectedCollaboration}
                    statusNames={availableStatuses}
                    areFiltersApplied={areFiltersApplied}
                    handleSearchPhraseChanged={handleSearchPhraseChanged}
                    checkedItems={checkedItems}
                    handleItemChecked={handleItemChecked}
                    clearCheckedItems={clearCheckedItems}
                    onDeleteItems={onDeleteItems}
                    onArchiveItems={onArchiveItems}
                    allAvailableAssignees={allAvailableAssignees}
                    bulkActionSelectedAssignee={bulkActionSelectedAssignee}
                    onBulkActionSelectedAssignee={onBulkActionSelectedAssignee}
                    areAllCheckedTasksArchived={areAllCheckedTasksArchived}
                    bulkActionSelectedStatus={bulkActionSelectedStatus}
                    onBulkActionSelectedStatus={onBulkActionSelectedStatus}
                    onBulkActionTagsChange={onBulkActionTagsChange}
                    onBulkActionDateChange={onBulkActionDateChange}
                    onCheckAllItemsClick={handleCheckAllItemsClick}
                    onSortClick={handleSortClick}
                    sortSettings={sortSettings}
                    onDateChange={handleDateChange}
                    onAssigneeChange={handleAssigneeChange}
                    onBulkFollowButtonClick={handleBulkFollowButtonClick}
                    onBulkActionFollowersChange={handleBulkFollowersChange}
                    onStatusChange={handleStatusChange}
                    areAllTasksFollowed={areAllTasksFollowed}
                    isFollowingInProgress={isFollowingInProgress}
                    taskRowCheckboxDataId={taskRowCheckboxDataId}
                    onExport={handleExport}
                    toReports={toReports}
                />
                {selectedCollaboration && (
                    <StyledCollaborationWindowContainer>
                        <CollaborationWindow onGoToCanvas={handleGoToCanvas} />
                    </StyledCollaborationWindowContainer>
                )}
            </PageContent>
        </ProjectPage>
    )
}

export default ListView
